'use client';

import React, { Suspense } from "react";
import { motion } from "framer-motion";
import { MessageSquare, Clock, AlertTriangle, UserX } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";

const problems = [
  {
    icon: MessageSquare,
    title: "120 mensagens por dia",
    description: "Um volume insustentável de mensagens no WhatsApp profissional.",
    area: "a",
  },
  {
    icon: Clock,
    title: "70% de dúvidas repetitivas",
    description: "Perguntas como 'posso tomar banho?' ou 'quando tirar os pontos?'.",
    area: "b",
  },
  {
    icon: AlertTriangle,
    title: "Risco de complicações",
    description: "Sinais de problemas sérios podem passar despercebidos no meio de tantas mensagens.",
    area: "c",
  },
  {
    icon: UserX,
    title: "Sobrecarga da equipe",
    description: "A secretária fica sobrecarregada, resultando em perda de eficiência clínica.",
    area: "d",
  },
];

const problemContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2, when: "beforeChildren" },
  },
  exit: {
    opacity: 0,
    transition: { staggerChildren: 0.1, staggerDirection: -1, when: "afterChildren" },
  }
};

const problemItemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  exit: { opacity: 0, y: -30, transition: { duration: 0.3 } }
};

type ProblemProps = {
  isBot: boolean;
};

export default function Problem({ isBot }: ProblemProps) {
    const Wrapper = isBot ? "div" : motion.div;

    return (
    <section id="problem" className="relative py-10 sm:py-16 bg-secondary/50">
      <div
        className="container mx-auto px-4"
      >
        <Suspense fallback={null}>
          <Wrapper
            initial={isBot ? undefined : "hidden"}
            whileInView={isBot ? undefined : "visible"}
            exit={isBot ? undefined : "exit"}
            viewport={isBot ? undefined : { once: false, amount: 0.2 }}
            variants={isBot ? undefined : problemContainerVariants}
            className="mx-auto max-w-2xl text-center"
          >
            <Wrapper variants={isBot ? undefined : problemItemVariants}>
              <div className="flex items-center justify-center gap-2">
                <h2 className="font-headline text-3xl tracking-[-0.04em] leading-[1.1] text-black sm:text-4xl font-semibold">
                  O problema real
                </h2>
              </div>
            </Wrapper>
            <Wrapper variants={isBot ? undefined : problemItemVariants}>
              <p className="mt-4 text-lg text-foreground/70 tracking-tight">
                A comunicação pós-operatória manual é ineficiente, arriscada e impede o crescimento da sua prática médica.
              </p>
            </Wrapper>
          </Wrapper>
          <Wrapper
              initial={isBot ? undefined : "hidden"}
              whileInView={isBot ? undefined : "visible"}
              exit={isBot ? undefined : "exit"}
              viewport={isBot ? undefined : { once: false, amount: 0.2 }}
              variants={isBot ? undefined : problemContainerVariants}
              className="mt-16 grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-[repeat(12,1fr)] lg:grid-rows-2"
          >
            {problems.map((problem, index) => (
                <Wrapper
                  key={index}
                  variants={isBot ? undefined : problemItemVariants}
                  className={
                      cn("lg:col-span-6", {
                          "lg:col-span-7": index === 0,
                          "lg:col-span-5": index === 1,
                          "lg:col-span-4": index === 2,
                          "lg:col-span-8": index === 3,
                      })
                  }
                >
                  <Card className={cn("text-left h-full bg-card border-border hover:border-primary hover:shadow-lg transition-all", index % 2 === 0 ? "message-bubble" : "message-bubble-right")}>
                      <CardContent className="p-6 flex flex-col justify-center">
                          <div className="flex items-start gap-4">
                              <div className="flex-shrink-0 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                                  <problem.icon className="h-6 w-6 text-primary" />
                              </div>
                              <div>
                                  <h3 className="font-semibold text-lg text-foreground tracking-tight">
                                    {problem.title}
                                  </h3>
                                  <p className="mt-1 text-muted-foreground tracking-tight">
                                    {problem.description}
                                  </p>
                              </div>
                          </div>
                      </CardContent>
                  </Card>
                </Wrapper>
            ))}
          </Wrapper>
        </Suspense>
      </div>
    </section>
  );
}
